import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BAR, CHEVRON, CLICKABLE, DISABLED } from "../styles";
import { ProgressStrip } from "./ProgressStrip";

type Props = {
  online: boolean;
  presenceLabel: string;
  webStateLabel: string;
  step: number;
  /** the mini strip only shows while collapsed: the panel owns the full one */
  showProgress: boolean;
  expanded: boolean;
  /** false unless the selected headset is online over a live socket */
  canExpand: boolean;
  onToggleExpanded: () => void;
  /** the headset combobox, rendered by the dock root */
  picker: ReactNode;
  /** Start and Skip, likewise */
  commands: ReactNode;
};

/**
 * The always-visible row. Everything here works without expanding the dock.
 *
 * The picker and the commands arrive as nodes rather than as their own props: this
 * component owns the row, not what sits in it, and every value they need is already
 * in HeadsetControlDock. Passing them through here would have meant nine props it
 * never reads.
 */
export function DockBar({
  online,
  presenceLabel,
  webStateLabel,
  step,
  showProgress,
  expanded,
  canExpand,
  onToggleExpanded,
  picker,
  commands,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className={BAR}>
      <span className="flex items-center gap-[8px] flex-none">
        <span
          className={`w-[9px] h-[9px] rounded-full flex-none ${
            online ? "bg-[#00A58D]" : "bg-[#949595]"
          }`}
        />
        <span
          className={`text-[10px] font-semibold tracking-[0.08em] ${
            online ? "text-[#00A58D]" : "text-[#949595]"
          }`}
        >
          {presenceLabel}
        </span>
      </span>

      {picker}

      <div className="flex flex-col gap-[6px] min-w-0 flex-initial">
        <button
          type="button"
          className={`text-[12px] font-normal text-[#949595] whitespace-nowrap overflow-hidden text-ellipsis min-w-0 bg-transparent border-none p-0 text-left ${
            canExpand ? CLICKABLE : "cursor-default"
          }`}
          disabled={!canExpand}
          onClick={onToggleExpanded}
        >
          {webStateLabel}
        </button>
        {showProgress && <ProgressStrip step={step} mini />}
      </div>

      <span className="flex-1 min-w-[8px] max-[640px]:hidden" />

      <div className="flex gap-[8px] flex-none">{commands}</div>

      <button
        type="button"
        className={`${CHEVRON} ${canExpand ? CLICKABLE : DISABLED}`}
        disabled={!canExpand}
        aria-label={t(expanded ? "HeadsetDock.Collapse" : "HeadsetDock.Expand")}
        aria-expanded={expanded}
        onClick={onToggleExpanded}
      >
        {expanded ? "▾" : "▴"}
      </button>
    </div>
  );
}
